import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import NotebookView from '../NotebookView';
import ChatPanel from '../ChatPanel';
import StatusBar from '../StatusBar';
import NotebookSidebar from '../NotebookSidebar';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE_URL, WS_BASE } from '../config';
import ShareButton from './ShareButton';
import Settings from './Settings';

const API_BASE = API_BASE_URL;

const ADMIN_EMAILS = ["team@scifi.ink"];

function Workspace() {
    const { user, token, logout } = useAuth();
    const [notebooks, setNotebooks] = useState([]);
    const [currentNotebook, setCurrentNotebook] = useState(null);
    const [notebookData, setNotebookData] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [status, setStatus] = useState({ message: 'Ready', type: 'idle' });
    const [showSidebar, setShowSidebar] = useState(true);
    const [isLoadingNotebook, setIsLoadingNotebook] = useState(false);

    // WebSocket state
    const wsRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const [liveStatus, setLiveStatus] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [pendingChat, setPendingChat] = useState(null);
    const [isInitializingQuery, setIsInitializingQuery] = useState(null);

    // Progress tracking state
    const [progressInfo, setProgressInfo] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [executingCells, setExecutingCells] = useState(new Set());

    // Settings modal
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // Auto-scroll state
    const notebookScrollRef = useRef(null);
    const [autoScroll, setAutoScroll] = useState(true);
    const [cellCount, setCellCount] = useState(0);
    const scrollTimeoutRef = useRef(null);

    // Subscription state
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    useEffect(() => {
        checkSubscription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const checkSubscription = async () => {
        if (!user) return;
        try {
            const response = await axios.get(`${API_BASE}/api/subscription/status/${user.id}`);
            setSubscriptionStatus(response.data);
        } catch (error) {
            console.error('Error checking subscription:', error);
        }
    };


    const deleteAllSessions = async () => {
        const confirmed = window.confirm('Are you sure you want to delete all active sessions? This action cannot be undone.');
        if (!confirmed) return;

        try {
            setStatus({ message: 'Deleting all sessions...', type: 'loading' });
            const response = await axios.get(`${API_BASE}/sessions`);
            const sessions = response.data;

            if (sessions.length === 0) {
                setStatus({ message: 'No sessions to delete', type: 'idle' });
                setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 2000);
                return;
            }

            // Delete all sessions
            await Promise.all(
                sessions.map(session => axios.delete(`${API_BASE}/sessions/${session.session_id}`))
            );

            setStatus({ message: `Deleted ${sessions.length} session(s)`, type: 'success' });
            setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 2000);
        } catch (error) {
            console.error('Error deleting all sessions:', error);
            setStatus({ message: 'Error deleting sessions', type: 'error' });
            setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 3000);
        }
    };

    useEffect(() => {
        loadNotebooks();
        
        // Check for pending query from landing page
        const pendingQuery = sessionStorage.getItem('pendingQuery');
        if (pendingQuery) {
            // Clear it immediately to prevent re-execution
            sessionStorage.removeItem('pendingQuery');
            
            // Create new notebook and send query
            handlePendingQuery(pendingQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentNotebook) {
            loadNotebook(currentNotebook);
            loadChatHistory(currentNotebook);
            connectWebSocket(currentNotebook);

            const interval = setInterval(() => {
                autoSave();
            }, 30000);

            return () => {
                clearInterval(interval);
                disconnectWebSocket();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentNotebook]);

    useEffect(() => {
        if (notebookData?.cells) {
            const newCellCount = notebookData.cells.length;

            if (newCellCount > cellCount && autoScroll) {
                scrollToBottom();
            }

            setCellCount(newCellCount);
        }
    }, [notebookData?.cells, autoScroll, cellCount]);

    const scrollToBottom = () => {
        if (notebookScrollRef.current) {
            setTimeout(() => {
                notebookScrollRef.current?.scrollTo({
                    top: notebookScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 100);
        }
    };

    const handleNotebookScroll = useCallback((e) => {
        const element = e.target;
        const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        if (!isAtBottom) {
            setAutoScroll(false);
        } else {
            scrollTimeoutRef.current = setTimeout(() => {
                setAutoScroll(true);
            }, 500);
        }
    }, []);

    const connectWebSocket = useCallback((notebookId) => {
        if (wsRef.current) {
            wsRef.current.close();
        }

        const ws = new WebSocket(`${WS_BASE}/ws/chat/${notebookId}${token ? `?token=${token}` : ''}`);

        ws.onopen = () => {
            console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
        };

        ws.onclose = () => {
            console.log('WebSocket disconnected');
            setSessionId(null);
            setIsInitializingQuery(null);
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setStatus({ message: 'Connection error', type: 'error' });
            setIsInitializingQuery(null);
        };

        wsRef.current = ws;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const disconnectWebSocket = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setSessionId(null);
    }, []);

    const handleWebSocketMessage = useCallback((data) => {
        if (data.type !== 'connected') {
            setIsInitializingQuery(null);
        }

        switch (data.type) {
            case 'connected':
                setSessionId(data.session_id);
                console.log('Session ID:', data.session_id);
                break;

            case 'typing':
                setIsTyping(data.is_typing);
                if (data.is_typing) {
                    setLiveStatus('✍️ AI is typing...');
                    setStatus({ message: '✍️ AI is typing...', type: 'loading' });
                }
                break;

            case 'action_progress':
                const progressMsg = `${data.action_type} (${data.current}/${data.total})`;
                setProgressInfo({
                    current: data.current,
                    total: data.total,
                    actionType: data.action_type
                });
                setLiveStatus(`⚙️ ${progressMsg}`);
                setStatus({ message: `⚙️ ${progressMsg}`, type: 'loading' });
                break;

            case 'cell_executing':
                setExecutingCells(prev => new Set([...prev, data.cell_id]));
                setLiveStatus(`▶️ Executing cell ${data.action_index}/${data.total_actions}...`);
                setStatus({ message: `▶️ Executing cell...`, type: 'loading' });
                break;

            case 'cell_execution_complete':
                setExecutingCells(prev => {
                    const next = new Set(prev);
                    next.delete(data.cell_id);
                    return next;
                });

                if (!data.success && data.error) {
                    console.error(`Cell ${data.cell_id} execution failed:`, data.error);
                    // Update cell with error
                    setNotebookData(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            cells: prev.cells.map(cell =>
                                cell.cell_id === data.cell_id
                                    ? { ...cell, error: data.error }
                                    : cell
                            )
                        };
                    });
                } else if (data.execution_time_ms) {
                    console.log(`Cell ${data.cell_id} executed in ${data.execution_time_ms.toFixed(2)}ms`);
                }
                break;

            case 'status':
                setLiveStatus(data.message);
                setStatus({ message: data.message, type: 'loading' });
                break;

            case 'llm_response':
                setChatHistory(prev => [...prev, {
                    role: 'assistant',
                    content: data.text,
                    metadata: {
                        actions: data.actions,
                        iteration: data.iteration
                    }
                }]);

                if (data.actions && Array.isArray(data.actions)) {
                    data.actions.forEach(action => {
                        if (action.action === 'add_cell') {
                            setNotebookData(prev => {
                                if (!prev) return prev;

                                const newCell = {
                                    cell_id: action.cell_id || `temp_${Date.now()}`,
                                    cell_type: action.cell_type || 'code',
                                    content: action.content || '',
                                    output: null,
                                    execution_count: null
                                };

                                return {
                                    ...prev,
                                    cells: [...prev.cells, newCell]
                                };
                            });
                        }
                    });
                }
                break;

            case 'action_result':
                console.log('Action result:', data.action, data.result);

                if (data.result && data.result.status === 'success') {
                    if (data.action.action === 'add_cell' && data.result.execution_result) {
                        setNotebookData(prev => {
                            if (!prev) return prev;

                            const cellId = data.result.cell_id;

                            return {
                                ...prev,
                                cells: prev.cells.map(cell => {
                                    if (cell.cell_id === cellId) {
                                        return {
                                            ...cell,
                                            output: data.result.execution_result.output || null,
                                            execution_count: (cell.execution_count || 0) + 1,
                                            error: data.result.execution_result.error || null
                                        };
                                    }
                                    return cell;
                                })
                            };
                        });
                    }
                }
                break;

            case 'cell_updated':
                setNotebookData(prev => {
                    if (!prev) return prev;

                    const cellExists = prev.cells.some(cell => cell.cell_id === data.cell.cell_id);

                    if (cellExists) {
                        return {
                            ...prev,
                            cells: prev.cells.map(cell =>
                                cell.cell_id === data.cell.cell_id ? data.cell : cell
                            )
                        };
                    } else {
                        return {
                            ...prev,
                            cells: [...prev.cells, data.cell]
                        };
                    }
                });
                console.log('Cell updated:', data.cell.cell_id);
                break;

            case 'notebook_updated':
                setNotebookData(data.notebook);
                console.log('Notebook updated:', data.notebook.id, 'cells:', data.notebook.cells.length);
                break;

            case 'complete':
                setIsProcessing(false);
                setLiveStatus(null);
                setProgressInfo(null);
                setIsTyping(false);
                setExecutingCells(new Set());
                setStatus({ message: data.message, type: 'success' });
                setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 2000);
                break;

            case 'cancelled':
                setIsProcessing(false);
                setLiveStatus(null);
                setProgressInfo(null);
                setIsTyping(false);
                setExecutingCells(new Set());
                setStatus({ message: data.message, type: 'idle' });
                break;

            case 'error':
                setIsProcessing(false);
                setLiveStatus(null);
                setProgressInfo(null);
                setIsTyping(false);
                setExecutingCells(new Set());
                setStatus({ message: data.message, type: 'error' });
                break;

            default:
                console.log('Unknown message type:', data.type, data);
        }
    }, []);


    const loadNotebooks = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/notebooks`);
            setNotebooks(response.data);
        } catch (error) {
            console.error('Error loading notebooks:', error);
        }
    };

    const loadNotebook = async (notebookId) => {
        try {
            setIsLoadingNotebook(true);
            setStatus({ message: 'Loading notebook...', type: 'loading' });
            const response = await axios.get(`${API_BASE}/api/notebooks/${notebookId}`);
            setNotebookData(response.data);
            setStatus({ message: 'Ready', type: 'idle' });
        } catch (error) {
            console.error('Error loading notebook:', error);
            setStatus({ message: 'Error loading notebook', type: 'error' });
        } finally {
            setIsLoadingNotebook(false);
        }
    };

    const loadChatHistory = async (notebookId) => {
        try {
            const response = await axios.get(`${API_BASE}/api/notebooks/${notebookId}/chat`);
            setChatHistory(response.data);
        } catch (error) {
            console.error('Error loading chat history:', error);
        }
    };

    const createNotebook = async (title = 'Untitled Notebook') => {
        try {
            const response = await axios.post(`${API_BASE}/api/notebooks`, { title });
            await loadNotebooks();
            setCurrentNotebook(response.data.id);
            return response.data.id;
        } catch (error) {
            console.error('Error creating notebook:', error);
            return null;
        }
    };

    const handlePendingQuery = async (query) => {
        try {
            setIsInitializingQuery("Creating your isolated environment...");
            // Create a new notebook
            const notebookTitle = 'New Notebook';
            const notebookId = await createNotebook(notebookTitle);
            
            if (notebookId) {
                setPendingChat(query);
            } else {
                setIsInitializingQuery(null);
            }
        } catch (error) {
            console.error('Error handling pending query:', error);
            setStatus({ message: 'Error processing your query', type: 'error' });
            setIsInitializingQuery(null);
        }
    };

    const deleteNotebook = async (notebookId) => {
        try {
            await axios.delete(`${API_BASE}/api/notebooks/${notebookId}`);
            await loadNotebooks();
            if (currentNotebook === notebookId) {
                setCurrentNotebook(null);
                setNotebookData(null);
            }
        } catch (error) {
            console.error('Error deleting notebook:', error);
        }
    };

    const addCell = async (cellType, content, cellId = null) => {
        if (!currentNotebook) return;

        try {
            await axios.post(`${API_BASE}/api/notebooks/${currentNotebook}/cells`, {
                cell_type: cellType,
                content,
                cell_id: cellId
            });
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error adding cell:', error);
        }
    };

    const updateCell = async (cellId, content) => {
        try {
            await axios.put(`${API_BASE}/api/cells/${cellId}`, { content });
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error updating cell:', error);
        }
    };

    const deleteCell = async (cellId) => {
        try {
            await axios.delete(`${API_BASE}/api/cells/${cellId}`);
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error deleting cell:', error);
        }
    };

    const executeCell = async (cellId) => {
        try {
            setStatus({ message: 'Executing cell...', type: 'loading' });
            await axios.post(`${API_BASE}/api/cells/${cellId}/execute`);
            await loadNotebook(currentNotebook);
            setStatus({ message: 'Cell executed', type: 'success' });
            setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 2000);
        } catch (error) {
            console.error('Error executing cell:', error);
            setStatus({ message: 'Execution error', type: 'error' });
        }
    };

    const moveCell = async (cellId, direction) => {
        if (!currentNotebook) return;
        try {
            await axios.post(`${API_BASE}/api/notebooks/${currentNotebook}/cells/reorder`, {
                cell_id: cellId,
                direction: direction
            });
            // Update local state optimistically or just reload
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error moving cell:', error);
            setStatus({ message: 'Error moving cell', type: 'error' });
        }
    };

    const sendChatMessage = useCallback((message) => {
        if (!currentNotebook || !wsRef.current) return;

        if (wsRef.current.readyState !== WebSocket.OPEN) {
            setStatus({ message: 'Not connected', type: 'error' });
            return;
        }

        setChatHistory(prev => [...prev, { role: 'user', content: message }]);

        setIsProcessing(true);
        setLiveStatus('🤔 LLM is thinking...');
        setStatus({ message: '🤔 LLM is thinking...', type: 'loading' });

        wsRef.current.send(JSON.stringify({
            type: 'chat',
            message: message
        }));
    }, [currentNotebook]);

    useEffect(() => {
        if (pendingChat && currentNotebook && sessionId) {
            setIsInitializingQuery("Connecting to AI Copilot...");
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                sendChatMessage(pendingChat);
                setPendingChat(null);
                setIsInitializingQuery("Waiting for AI Copilot's response...");
            }
        }
    }, [pendingChat, currentNotebook, sessionId, sendChatMessage]);

    const cancelChat = useCallback(async () => {
        if (!sessionId) return;

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'cancel' }));
        }

        try {
            await axios.post(`${API_BASE}/api/chat/cancel`, { session_id: sessionId });
        } catch (error) {
            console.error('Error cancelling chat:', error);
        }

        setIsProcessing(false);
        setLiveStatus(null);
        setProgressInfo(null);
        setIsTyping(false);
        setExecutingCells(new Set());
        setStatus({ message: 'Cancelled', type: 'idle' });
    }, [sessionId]);

    const autoSave = () => {
        if (currentNotebook && notebookData) {
            setStatus({ message: 'Auto-saved', type: 'success' });
            setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 1500);
        }
    };

    const renameNotebook = async (notebookId, newTitle) => {
        try {
            await axios.put(`${API_BASE}/api/notebooks/${notebookId}`, { title: newTitle });
            await loadNotebooks();
            if (currentNotebook === notebookId) {
                setNotebookData(prev => ({ ...prev, title: newTitle }));
            }
        } catch (error) {
            console.error('Error renaming notebook:', error);
        }
    };

    const getTitle = () => {
        if (!currentNotebook) return;
        return notebooks.find(notebook => notebook.id === currentNotebook).title;
    };


    return (
        <div className="flex h-screen bg-gray-50">
            {showSidebar && (
                <NotebookSidebar
                    notebooks={notebooks}
                    currentNotebook={currentNotebook}
                    onSelectNotebook={setCurrentNotebook}
                    onCreateNotebook={createNotebook}
                    onDeleteNotebook={deleteNotebook}
                    onRenameNotebook={renameNotebook}
                    onCloseSidebar={() => setShowSidebar(false)}
                />
            )}

            <div className="flex-1 flex flex-col">
                <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {!showSidebar && (
                            <button
                                onClick={() => setShowSidebar(true)}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        )}
                        <h1 className="text-2xl font-bold text-gray-900">
                            {notebookData?.title || 'sciFi'}
                        </h1>
                        {currentNotebook && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={deleteAllSessions}
                                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded border border-red-200 transition-colors flex items-center gap-1"
                                    title="Delete All Sessions"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete All Sessions
                                </button>
                                <ShareButton notebookId={currentNotebook} notebookTitle={getTitle()} />
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <StatusBar status={status} progressInfo={progressInfo} />
                        {/* Temporarily hidden for hackathon */}
                        {/* Subscription Status Badge */}
                        {/* {subscriptionStatus && (
                            <div className="flex items-center gap-2">
                                { (subscriptionStatus.subscription_plan === 'pro' || subscriptionStatus.subscription_plan === 'premium')  && subscriptionStatus.is_active ? (
                                    <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full font-medium border border-green-200">
                                        Pro Active
                                    </span>
                                ) : (
                                    <Link
                                        to="/subscription"
                                        className="px-3 py-1 text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        Upgrade to Pro
                                    </Link>
                                )}
                            </div>
                        )} */}
                        <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
                            <Link
                                to="/hackathon/dashboard"
                                className="px-3 py-1 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded border border-blue-200 transition-colors flex items-center gap-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Dashboard
                            </Link>
                            {user && ADMIN_EMAILS.includes(user.email) && (
                                <Link
                                    to="/admin"
                                    className="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded border border-indigo-200 transition-colors flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
                                    </svg>
                                    Admin
                                </Link>
                            )}

                            <button
                                onClick={() => setShowSettingsModal(true)}
                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-200 transition-colors flex items-center gap-1"
                                title="Settings"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Settings
                            </button>

                            <button
                                onClick={logout}
                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-200 transition-colors flex items-center gap-1"
                                title="Logout"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Logout
                            </button>
                        </div>
                    </div>
                </header>

                <div className="flex-1 flex overflow-hidden relative">
                    {isLoadingNotebook && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                                <p className="text-gray-700 font-medium">Loading notebook...</p>
                            </div>
                        </div>
                    )}
                    <div className={`absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm transition-all duration-500 ${isInitializingQuery ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6 shadow-lg"></div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 animate-pulse">Setting up your workspace</h2>
                        <p className="text-gray-500 text-center max-w-md animate-pulse mb-6">
                            {isInitializingQuery || "Initializing..."}
                        </p>
                        <button 
                            onClick={() => {
                                setIsInitializingQuery(null);
                                setPendingChat(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
                        >
                            Cancel
                        </button>
                    </div>
                    <div
                        ref={notebookScrollRef}
                        onScroll={handleNotebookScroll}
                        className="flex-1 overflow-auto"
                    >
                        {notebookData ? (
                            <NotebookView
                                notebook={notebookData}
                                onAddCell={addCell}
                                onUpdateCell={updateCell}
                                onDeleteCell={deleteCell}
                                onExecuteCell={executeCell}
                                onMoveCell={moveCell}
                                executingCells={executingCells}
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <div className="text-center">
                                    <p className="text-lg mb-4">No notebook selected</p>
                                    <button
                                        onClick={() => createNotebook()}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Create New Notebook
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {currentNotebook && (
                        <ChatPanel
                            chatHistory={chatHistory}
                            onSendMessage={sendChatMessage}
                            status={status}
                            onCancel={cancelChat}
                            isProcessing={isProcessing}
                            liveStatus={liveStatus}
                            isTyping={isTyping}
                            progressInfo={progressInfo}
                        />
                    )}
                </div>
            </div>

            {showSettingsModal && (
                <Settings
                    isOpen={showSettingsModal}
                    onClose={() => setShowSettingsModal(false)}
                />
            )}
        </div>
    );
}

export default Workspace;